// src/services/otherSchoolAlert.ts
// --------------------------------
// PURE helpers for the "Other school → alert" (John, 9 Aug 2026). A school entered
// via the "Other" free-text chip isn't in the schools table; at request SUBMIT we
// flag any confirmed, non-special school whose name isn't found in the table, and the
// wizard emails the System Alert address so staff can add it. Detection is stateless
// (Option B) — no stored flag — so it survives a saved-and-resumed draft. The table
// lookup + the email send live in the caller (RequestWizard); these helpers are pure
// and unit-tested.

// Special school values that are NOT real schools (the picker chips), so never "Other".
// NB "Other" itself is a MODE, not a stored value — the stored value is the free text.
export const SCHOOL_SPECIAL_VALUES = ["Home Schooling", "TAFE", "Not applicable"];

// The picker stores a picked school as "Name — Suburb"; strip the suburb so the name
// can be matched against the schools table (which stores the name only).
export function stripSchoolSuburb(name: string | null | undefined): string {
  return String(name || "")
    .split(" — ")[0]
    .trim();
}

export interface OtherSchoolCandidate {
  childLabel: string; // "Child 1", …
  schoolName: string; // as stored (may include the "— Suburb" suffix)
  lookupName: string; // stripped name to check against the schools table
}

// Children whose confirmed school should be checked against the table (i.e. could be
// an "Other"): non-empty, deliberately confirmed, and not one of the special chips.
export function otherSchoolCandidates(
  children: Array<{ schoolName?: string | null; schoolConfirmed?: boolean }>
): OtherSchoolCandidate[] {
  const out: OtherSchoolCandidate[] = [];
  (children || []).forEach((child, i) => {
    const name = String(child?.schoolName || "").trim();
    if (!name) return;
    if (child?.schoolConfirmed === false) return; // not a deliberate choice
    if (SCHOOL_SPECIAL_VALUES.includes(name)) return; // a chip, not a school
    out.push({
      childLabel: `Child ${i + 1}`,
      schoolName: name,
      lookupName: stripSchoolSuburb(name),
    });
  });
  return out;
}

// Given the candidates + the names that EXIST in the table, the ones NOT found are
// "Other" (free-typed) schools to alert on. Case-insensitive match.
export function otherSchoolsToAlert(
  candidates: OtherSchoolCandidate[],
  existingNames: string[]
): OtherSchoolCandidate[] {
  const existing = new Set(
    (existingNames || []).map((n) =>
      String(n || "")
        .trim()
        .toLowerCase()
    )
  );
  return candidates.filter((c) => !existing.has(c.lookupName.toLowerCase()));
}
